import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Capacitor } from "@capacitor/core";
import { Camera, CameraResultType, CameraSource } from "@capacitor/camera";

// REISOR_STAGE123_DRIVER_REGISTRATION_SIMPLE_ONBOARDING_V1
const REQUIRED_GROUPS = [
  {
    id: "driver",
    titleKey: "docsCamera.driverGroup",
    titleFallback: "Документы водителя",
    items: [
      { kind: "passport1", key: "docs.passport1", fallback: "Паспорт" },
    ],
  },
  {
    id: "vehicleDocs",
    titleKey: "docsCamera.vehicleDocsGroup",
    titleFallback: "Документы автомобиля",
    items: [
      { kind: "sts1", key: "docs.sts1", fallback: "СТС — лицевая сторона" },
      { kind: "sts2", key: "docs.sts2", fallback: "СТС — обратная сторона" },
    ],
  },
  {
    id: "vehiclePhotos",
    titleKey: "docsCamera.vehiclePhotosGroup",
    titleFallback: "Фотографии автомобиля",
    items: [
      { kind: "carPhoto", key: "docs.carPhoto", fallback: "Фотография автомобиля" },
    ],
  },
];

const REQUIRED = REQUIRED_GROUPS.flatMap((group) => group.items);

function safeJsonParse(value) {
  try { return JSON.parse(String(value || "")); } catch { return null; }
}

function getStoredDriverId() {
  const id = (localStorage.getItem("driver_id") || "").trim();
  if (id) return id;
  const driver = safeJsonParse(localStorage.getItem("driver_data")) || {};
  return String(driver._id || driver.id || driver.driverId || "").trim();
}

function getStoredToken() {
  return (localStorage.getItem("driver_token") || "").trim();
}

function isNativeCameraRuntime() {
  try { return Capacitor.isNativePlatform(); } catch { return false; }
}

// REISOR_CAMERA_RUNTIME_DIAG_V1
const CAMERA_DIAG_STORAGE_KEY = "reisor_camera_diag_v1";

function cameraDiagError(error) {
  try {
    const out = {
      name: String(error?.name || ""),
      message: String(error?.message || error || ""),
      code: String(error?.code || ""),
    };
    if (error && typeof error === "object") {
      for (const key of Object.getOwnPropertyNames(error)) {
        if (key === "stack" || key in out) continue;
        const value = error[key];
        if (["string", "number", "boolean"].includes(typeof value)) out[key] = value;
      }
    }
    return out;
  } catch {
    return { message: String(error || "UNKNOWN_ERROR") };
  }
}

function cameraDiagStored() {
  try {
    const value = JSON.parse(localStorage.getItem(CAMERA_DIAG_STORAGE_KEY) || "[]");
    return Array.isArray(value) ? value.slice(-30) : [];
  } catch {
    return [];
  }
}

async function blobFromWebPath(webPath) {
  const response = await fetch(String(webPath || ""));
  if (!response.ok) throw new Error(`PHOTO_FETCH_${response.status}`);
  return await response.blob();
}

export default function DriverDocsCamera({ profilePayload = null, onSubmitted }) {
  const { t } = useTranslation();
  const tr = (key, fallback) => {
    try { return t(key, { defaultValue: fallback }); } catch { return fallback; }
  };

  const [driverId, setDriverId] = useState(() => getStoredDriverId());
  const [token, setToken] = useState(() => getStoredToken());
  const [status, setStatus] = useState("");
  const [documents, setDocuments] = useState({});
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const [camOpen, setCamOpen] = useState(false);
  const [camKind, setCamKind] = useState("");
  const [camErr, setCamErr] = useState("");
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const htmlCameraRef = useRef(null);
  const htmlCameraKindRef = useRef("");
  const [cameraDiag, setCameraDiag] = useState(() => cameraDiagStored());

  function recordCameraDiag(step, details = {}) {
    const entry = {
      ts: new Date().toISOString(),
      step: String(step || ""),
      details,
    };
    try { console.log("[REISOR_CAMERA_DIAG]", entry); } catch {}
    setCameraDiag((previous) => {
      const next = [...previous, entry].slice(-30);
      try { localStorage.setItem(CAMERA_DIAG_STORAGE_KEY, JSON.stringify(next)); } catch {}
      return next;
    });
  }

  function clearCameraDiag() {
    setCameraDiag([]);
    try { localStorage.removeItem(CAMERA_DIAG_STORAGE_KEY); } catch {}
    setErr("");
    setCamErr("");
  }

  async function copyCameraDiag() {
    const text = JSON.stringify(cameraDiag, null, 2);
    try {
      await navigator.clipboard.writeText(text);
      setErr("CAMERA DIAG скопирован в буфер.");
    } catch {
      setErr(text);
    }
  }

  function startHtmlCameraProbe(kind) {
    htmlCameraKindRef.current = kind;
    recordCameraDiag("P6_HTML_CAPTURE_CLICK", {
      kind,
      platform: (() => { try { return Capacitor.getPlatform(); } catch { return "unknown"; } })(),
    });
    const input = htmlCameraRef.current;
    if (!input) {
      recordCameraDiag("P6_HTML_CAPTURE_INPUT_MISSING");
      setErr("CAMERA DIAG: HTML input missing");
      return;
    }
    input.value = "";
    input.click();
  }

  async function onHtmlCameraProbe(event) {
    const kind = htmlCameraKindRef.current;
    const file = event?.target?.files?.[0] || null;
    if (!file) {
      recordCameraDiag("P6_HTML_CAPTURE_CANCEL_OR_EMPTY", { kind });
      return;
    }
    recordCameraDiag("P6_HTML_CAPTURE_FILE", {
      kind,
      type: String(file.type || ""),
      size: Number(file.size || 0),
    });
    try {
      await uploadBlob(kind, file);
      recordCameraDiag("P6_HTML_CAPTURE_UPLOAD_OK", { kind });
    } catch (error) {
      const detail = cameraDiagError(error);
      recordCameraDiag("P6_HTML_CAPTURE_ERROR", detail);
      setErr(`CAMERA DIAG HTML ERROR\n${JSON.stringify(detail, null, 2)}`);
    } finally {
      if (event?.target) event.target.value = "";
    }
  }

  const authHeaders = useMemo(() => {
    const headers = {};
    if (token) headers.Authorization = /^bearer\s+/i.test(token) ? token : `Bearer ${token}`;
    return headers;
  }, [token]);

  const progress = useMemo(() => {
    const done = REQUIRED.reduce((count, item) => (
      String(documents?.[item.kind] || "").trim() ? count + 1 : count
    ), 0);
    return { done, total: REQUIRED.length };
  }, [documents]);

  const normalizedStatus = String(status || "").toLowerCase();
  const locked = normalizedStatus === "approved" || normalizedStatus === "blocked";
  const alreadyPending = normalizedStatus === "pending" || submitted;

  async function refreshProfile() {
    setErr("");
    const id = getStoredDriverId();
    const storedToken = getStoredToken();
    if (id) setDriverId(id);
    if (storedToken) setToken(storedToken);
    if (!id) return;

    const response = await fetch(`/api/drivers/${encodeURIComponent(id)}/profile`, {
      method: "GET",
      credentials: "include",
      headers: storedToken ? { Authorization: /^bearer\s+/i.test(storedToken) ? storedToken : `Bearer ${storedToken}` } : {},
      cache: "no-store",
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || data.ok === false) throw new Error(data.error || `HTTP_${response.status}`);

    const nextStatus = data.status || data?.driver?.status || "";
    setStatus(nextStatus);
    setDocuments(data.documents || data?.driver?.documents || {});
    if (String(nextStatus).toLowerCase() === "pending") setSubmitted(true);
  }

  useEffect(() => {
    refreshProfile().catch((error) => setErr(String(error?.message || error)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const onWindowError = (event) => {
      recordCameraDiag("P7_WINDOW_ERROR", {
        message: String(event?.message || ""),
        source: String(event?.filename || ""),
        line: Number(event?.lineno || 0),
        column: Number(event?.colno || 0),
      });
    };
    const onUnhandled = (event) => {
      recordCameraDiag("P7_UNHANDLED_REJECTION", cameraDiagError(event?.reason));
    };
    window.addEventListener("error", onWindowError);
    window.addEventListener("unhandledrejection", onUnhandled);
    return () => {
      window.removeEventListener("error", onWindowError);
      window.removeEventListener("unhandledrejection", onUnhandled);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function openCamera(kind) {
    setErr("");
    setCamErr("");
    setCamKind(kind);

    const nativeRuntime = isNativeCameraRuntime();
    let platform = "unknown";
    let pluginAvailable = "unknown";
    try { platform = Capacitor.getPlatform(); } catch {}
    try { pluginAvailable = Capacitor.isPluginAvailable("Camera"); } catch {}

    recordCameraDiag("P1_CLICK", {
      kind,
      nativeRuntime,
      platform,
      pluginAvailable,
      visibility: String(document?.visibilityState || ""),
      userAgent: String(navigator?.userAgent || "").slice(0, 240),
    });

    try {
      if (nativeRuntime) {
        try {
          const permissions = await Camera.checkPermissions();
          recordCameraDiag("P2_PERMISSIONS", permissions || {});
        } catch (permissionError) {
          recordCameraDiag("P2_PERMISSIONS_ERROR", cameraDiagError(permissionError));
        }

        recordCameraDiag("P3_GETPHOTO_START", { kind });
        const photo = await Camera.getPhoto({
          quality: 90,
          allowEditing: false,
          resultType: CameraResultType.Uri,
          source: CameraSource.Camera,
          correctOrientation: true,
          saveToGallery: false,
        });

        recordCameraDiag("P3_GETPHOTO_RESOLVED", {
          kind,
          hasWebPath: !!photo?.webPath,
          hasPath: !!photo?.path,
          format: String(photo?.format || ""),
          saved: !!photo?.saved,
        });

        const webPath = String(photo?.webPath || "").trim();
        if (!webPath) throw new Error("CAMERA_NO_WEBPATH");

        recordCameraDiag("P4_BLOB_FETCH_START", {
          scheme: webPath.split(":")[0] || "",
          length: webPath.length,
        });
        const blob = await blobFromWebPath(webPath);
        recordCameraDiag("P4_BLOB_FETCH_OK", {
          type: String(blob?.type || ""),
          size: Number(blob?.size || 0),
        });

        recordCameraDiag("P5_UPLOAD_CALL_START", { kind });
        await uploadBlob(kind, blob);
        recordCameraDiag("P5_UPLOAD_CALL_OK", { kind });
        return;
      }

      recordCameraDiag("P1_WEB_GETUSERMEDIA_START", { kind });
      setCamOpen(true);
      if (!navigator?.mediaDevices?.getUserMedia) throw new Error("CAMERA_UNAVAILABLE");

      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: "environment" } },
        audio: false,
      });
      recordCameraDiag("P1_WEB_GETUSERMEDIA_OK", { kind });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
    } catch (error) {
      const detail = cameraDiagError(error);
      const message = String(detail?.message || "").toLowerCase();
      recordCameraDiag("P_ERROR", detail);

      if (message.includes("cancel")) {
        setCamOpen(false);
        setCamKind("");
        setCamErr("");
        setErr(`CAMERA DIAG: пользователь закрыл камеру.\n${JSON.stringify(detail, null, 2)}`);
        return;
      }

      if (nativeRuntime) setCamOpen(false);
      const full = `CAMERA DIAG ERROR\nstep=P_ERROR\nplatform=${platform}\npluginAvailable=${pluginAvailable}\n${JSON.stringify(detail, null, 2)}`;
      setErr(full);

      if (message.includes("permission") || message.includes("denied")) {
        setCamErr(tr("docsCamera.cameraPermissionDenied", "Нет разрешения на использование камеры"));
      } else if (message.includes("camera_unavailable")) {
        setCamErr(tr("docsCamera.cameraUnavailable", "Камера недоступна"));
      } else {
        setCamErr(tr("docsCamera.cameraOpenFailed", "Не удалось открыть камеру"));
      }
    }
  }

  function closeCamera() {
    const stream = streamRef.current;
    if (stream) stream.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    setCamOpen(false);
    setCamKind("");
    setCamErr("");
  }

  async function uploadBlob(kind, blob) {
    recordCameraDiag("P5_UPLOAD_ENTER", {
      kind,
      driverIdPresent: !!driverId,
      tokenPresent: !!token,
      type: String(blob?.type || ""),
      size: Number(blob?.size || 0),
    });

    if (!driverId) throw new Error("DRIVER_ID_MISSING");

    setBusy(kind);
    setErr("");
    try {
      const formData = new FormData();
      formData.append("file", blob, `${kind}.jpg`);

      recordCameraDiag("P5_UPLOAD_HTTP_START", { kind });
      const response = await fetch(`/api/drivers/${encodeURIComponent(driverId)}/docs/${encodeURIComponent(kind)}`, {
        method: "POST",
        body: formData,
        credentials: "include",
        headers: authHeaders,
      });
      const data = await response.json().catch(() => ({}));

      recordCameraDiag("P5_UPLOAD_HTTP_RESULT", {
        kind,
        status: Number(response.status || 0),
        ok: !!response.ok,
        apiOk: data?.ok !== false,
        apiError: String(data?.error || "").slice(0, 180),
      });

      if (!response.ok || data.ok === false) throw new Error(data.error || `HTTP_${response.status}`);
      if (data.documents) setDocuments(data.documents);

      recordCameraDiag("P5_PROFILE_REFRESH_START", { kind });
      await refreshProfile();
      recordCameraDiag("P5_PROFILE_REFRESH_OK", { kind });
    } catch (error) {
      recordCameraDiag("P5_UPLOAD_ERROR", cameraDiagError(error));
      throw error;
    } finally {
      setBusy("");
    }
  }

  async function takeShot() {
    const video = videoRef.current;
    if (!video) return;

    const width = video.videoWidth || 720;
    const height = video.videoHeight || 1280;
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext("2d");
    context.drawImage(video, 0, 0, width, height);

    const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.9));
    if (!blob) throw new Error("CAPTURE_FAILED");
    await uploadBlob(camKind, blob);
    closeCamera();
  }

  async function submit() {
    if (!driverId || progress.done < progress.total || alreadyPending) return;

    setBusy("submit");
    setErr("");
    try {
      const response = await fetch(`/api/drivers/${encodeURIComponent(driverId)}/submit`, {
        method: "POST",
        credentials: "include",
        headers: { ...authHeaders, "Content-Type": "application/json" },
        body: JSON.stringify(profilePayload || {}),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.ok === false) throw new Error(data.error || `HTTP_${response.status}`);

      setStatus("pending");
      setSubmitted(true);
      try {
        const current = safeJsonParse(localStorage.getItem("driver_data")) || {};
        localStorage.setItem("driver_data", JSON.stringify({ ...current, status: "pending" }));
        window.dispatchEvent(new Event("gptcargo_driver_auth_changed"));
      } catch {}
      onSubmitted?.(data);
    } finally {
      setBusy("");
    }
  }

  return (
    <div className="mt-4 rounded-[28px] border border-white/10 bg-white/5 p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-xl font-black">{tr("docsCamera.onboardingTitle", "Документы водителя и автомобиля")}</div>
          <div className="mt-1 text-sm text-slate-300">
            {tr("docsCamera.onboardingHint", "Сделайте четыре фотографии. После последней фотографии отправьте анкету на проверку.")}
          </div>
        </div>
        <div className="shrink-0 rounded-full border border-sky-300/25 bg-sky-300/10 px-3 py-1 text-sm font-black text-sky-100">
          {progress.done}/{progress.total}
        </div>
      </div>

      <div className="mt-4 h-2 overflow-hidden rounded-full bg-slate-700/80">
        <div className="h-full rounded-full bg-sky-500 transition-all" style={{ width: `${(progress.done / progress.total) * 100}%` }} />
      </div>

      {err ? <div className="mt-3 whitespace-pre-wrap break-words rounded-xl border border-red-400/30 bg-red-950/35 p-3 text-sm text-red-200">{err}</div> : null}

      {false && cameraDiag.length ? (
        <div className="mt-3 rounded-2xl border border-amber-300/30 bg-amber-950/20 p-3">
          <div className="flex items-center justify-between gap-2">
            <div className="text-sm font-black text-amber-100">CAMERA DIAG 1.0.81</div>
            <div className="flex gap-2">
              <button type="button" className="rounded-lg bg-white/10 px-2 py-1 text-xs font-bold" onClick={copyCameraDiag}>Копировать</button>
              <button type="button" className="rounded-lg bg-white/10 px-2 py-1 text-xs font-bold" onClick={clearCameraDiag}>Очистить</button>
            </div>
          </div>
          <pre className="mt-2 max-h-56 overflow-auto whitespace-pre-wrap break-words text-[10px] leading-4 text-amber-50">{JSON.stringify(cameraDiag, null, 2)}</pre>
        </div>
      ) : null}

      <div className="mt-4 space-y-4">
        {REQUIRED_GROUPS.map((group) => (
          <section key={group.id} className="rounded-2xl border border-white/10 bg-black/20 p-3">
            <div className="mb-2 text-sm font-black text-sky-100">{tr(group.titleKey, group.titleFallback)}</div>
            <div className="space-y-2">
              {group.items.map((item) => {
                const url = String(documents?.[item.kind] || "").trim();
                const uploaded = !!url;
                return (
                  <div key={item.kind} className="flex items-center justify-between gap-3 rounded-xl border border-white/10 bg-slate-950/35 p-3">
                    <div className="min-w-0">
                      <div className="text-sm font-semibold">{tr(item.key, item.fallback)}</div>
                      <div className={`mt-1 text-xs ${uploaded ? "text-emerald-300" : "text-slate-400"}`}>
                        {uploaded ? tr("docsCamera.uploaded", "✅ Загружено") : tr("docsCamera.notUploaded", "Не загружено")}
                      </div>
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      {uploaded ? (
                        <a className="text-xs underline opacity-90" href={url} target="_blank" rel="noreferrer">
                          {tr("docsCamera.open", "Открыть")}
                        </a>
                      ) : null}
                      <button
                        type="button"
                        className="rounded-xl bg-white/10 px-3 py-2 text-sm font-bold disabled:opacity-50"
                        disabled={locked || alreadyPending || busy === item.kind}
                        onClick={() => startHtmlCameraProbe(item.kind)}
                      >
                        {busy === item.kind ? "..." : uploaded ? "Заменить файл" : "Прикрепить файл"}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        ))}
      </div>

      <input
        ref={htmlCameraRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(event) => onHtmlCameraProbe(event)}
      />

      {alreadyPending ? (
        <div className="mt-4 rounded-2xl border border-emerald-300/30 bg-emerald-300/10 p-4 text-center text-sm font-bold text-emerald-100">
          ✅ {tr("docsCamera.pendingNow", "Анкета отправлена и находится на проверке")}
        </div>
      ) : (
        <button
          type="button"
          className="mt-4 w-full rounded-2xl bg-emerald-600 px-4 py-4 text-base font-black text-white disabled:opacity-50"
          disabled={locked || progress.done < progress.total || busy === "submit"}
          onClick={() => submit().catch((error) => setErr(String(error?.message || error)))}
        >
          {busy === "submit"
            ? tr("docsCamera.sending", "Отправляем…")
            : progress.done < progress.total
              ? tr("docsCamera.completeFirst", `Загрузите ещё ${progress.total - progress.done}`)
              : tr("docsCamera.saveAndSend", "Сохранить и отправить на проверку")}
        </button>
      )}

      {camOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
          <div className="w-full max-w-md rounded-2xl border border-white/10 bg-slate-900 p-3">
            <div className="text-sm font-semibold">{tr("docsCamera.cameraSnapshot", "Фотография документа")}</div>
            <div className="mt-2 overflow-hidden rounded-xl border border-white/10">
              <video ref={videoRef} playsInline className="h-80 w-full object-cover" />
            </div>
            {camErr ? <div className="mt-2 text-xs text-red-300">{camErr}</div> : null}
            <div className="mt-3 flex gap-2">
              <button type="button" className="flex-1 rounded-xl bg-emerald-600 px-3 py-2 text-sm font-semibold disabled:opacity-50" disabled={!!camErr} onClick={() => takeShot().catch((error) => setCamErr(String(error?.message || error)))}>
                {tr("docsCamera.capture", "Сфотографировать")}
              </button>
              <button type="button" className="flex-1 rounded-xl bg-white/10 px-3 py-2 text-sm" onClick={closeCamera}>
                {tr("docsCamera.cancel", "Отмена")}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
